import {
  Alert,
  Box,
  Button,
  CircularProgress,
  type DialogProps,
  Divider,
  LinearProgress,
  Stack,
  type Theme,
  Typography,
  useTheme,
} from '@mui/material'
import { useQuery } from '@tanstack/react-query'
import { useCallback } from 'react'

import { useModal } from '@salsita-ai/platform-project-sdk/react'
import { Dialog, PhxIcon } from '@salsita-ai/platform-project-sdk/ui'

import { getCurrentShareBaseUrl } from '../shopify/share-link.js'
import { addVariantsToCart, getShopifyCartUrl } from '../shopify/shopify-ajax-client.js'
import { configuratorInterface } from '../systems/configurator-interface.js'

interface AddToCartDialogProps extends DialogProps {
  onBack: () => void
  shareLink: string
}

export const AddToCartDialog = (props: AddToCartDialogProps) => {
  const { onBack, open, shareLink } = props
  const { $productsForCart } = configuratorInterface.useRootEntityState()

  const theme = useTheme()

  const cartUrl = getShopifyCartUrl()

  const queryFn = useCallback(async () => {
    if (!cartUrl) {
      throw new Error('No Shopify cart is available')
    }
    if ($productsForCart.length === 0) {
      throw new Error('No Shopify product variants found in summary')
    }
    await addVariantsToCart($productsForCart, shareLink)
    return true
  }, [$productsForCart, cartUrl, shareLink])

  const { data, isError, error } = useQuery({
    queryKey: ['addVariantsToCart'],
    queryFn,
    retry: false,
  })

  const handleGoToCart = useCallback(() => {
    if (cartUrl) {
      window.open(cartUrl, '_self')
    }
  }, [cartUrl])

  return (
    <Dialog.Root isOpen={open} onClose={onBack}>
      <Dialog.ContentLayout
        controlsPosition="end"
        header={
          <Box
            component="div"
            sx={(theme: Theme) => ({
              backgroundColor: theme.palette.background.default,
              aspectRatio: '2.9',
              display: 'grid',
              placeItems: 'center',
              color: 'green',
            })}
          >
            <PhxIcon name="CheckCircle" style={{ fontSize: 200 }} />
          </Box>
        }
        body={
          <>
            <Typography variant="h4" gutterBottom id="success-dialog-title">
              Your wire balustrade components are being added to your shopping cart.
            </Typography>
            <Divider sx={{ mt: 2, mb: 2 }} />

            <Stack direction="column" spacing={1} sx={{ pl: 0.5 }}>
              {isError && (
                <Alert severity="error">
                  <Typography variant="body1">
                    Sorry, there was a problem. Please try again later. If the issue persists, contact support.
                  </Typography>
                  <Typography variant="body1">{error?.message}</Typography>
                </Alert>
              )}
              {!data && !isError && (
                <>
                  <Typography>Waiting for variants to be added to cart...</Typography>
                  <LinearProgress />
                </>
              )}
              {data && (
                <Typography variant="body1" gutterBottom sx={{ mb: 1 }}>
                  Successfully created, click the button below to go to your cart.
                </Typography>
              )}
            </Stack>
          </>
        }
        controls={
          <>
            <Button variant="text" onClick={onBack} startIcon={<PhxIcon name="ArrowLeft" />}>
              Back to Summary
            </Button>
            {cartUrl && (
              <Button
                disabled={!data}
                onClick={handleGoToCart}
                startIcon={
                  !data ? (
                    <CircularProgress size={20} sx={{ color: theme.palette.action.disabled }} />
                  ) : (
                    <PhxIcon name="ShoppingCartSimple" />
                  )
                }
              >
                Go to Cart
              </Button>
            )}
          </>
        }
      />
    </Dialog.Root>
  )
}

export default configuratorInterface.createPhoenixHandler('addToCart', () => {
  const { showModal } = useModal({ disableAutoDestroy: true })
  const apiClient = configuratorInterface.useAuthorizedClient()
  const { configurationId } = configuratorInterface.useDomain()
  const { configurationPersistSystem } = configuratorInterface.useDomainApplication()

  return {
    handler: useCallback(async () => {
      const revisionId = await configurationPersistSystem.save()
      const { link } = await apiClient.generateShareLink(
        configurationId,
        revisionId,
        24 * 90, // 90 days
        'o',
        getCurrentShareBaseUrl()
      )
      const modal = showModal(
        AddToCartDialog,
        {
          onBack: () => {
            modal.hide()
          },
          shareLink: link,
        },
        {}
      )
    }, [showModal, apiClient, configurationId, configurationPersistSystem]),
  }
})
